import { useMemo } from 'react';
import { Flex, Text, tokens } from '@optiaxiom/react';
import { IconBox } from '@optimizely/axiom-icons';
import { useConnection } from '@xyflow/react';

import { BadgeVariant } from '../nodes/components/node-parts/NodeBadges/NodeBadges.css';

import { useNodeValidation } from './useNodeValidation';
import { useWorkflow } from './useWorkflow';

export type UseNodeRendererProps = {
  id: string;
  type: string;
  data: Record<string, unknown>;
  selected?: boolean;
};

export type NodeBadgeData = {
  content: React.ReactNode;
  tooltip?: string;
  variant?: BadgeVariant;
  className?: string;
};

export type UseNodeRendererReturn = {
  // Node type info
  nodeType: ReturnType<ReturnType<typeof useWorkflow>['getNodeConfig']>;
  nodeTypeName: string;

  // Display info
  displayName: string;
  description: string;
  iconInfo?: {
    icon: React.ComponentType<{ size?: string | number }>;
    color: keyof typeof tokens.colors;
  };
  category?: string;
  label: string;

  // Validation
  hasErrors: boolean;
  isUnsavedAsync: boolean;

  // Connection state
  isSourceNode: boolean;

  // Event handlers
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;

  // Generic addon badges (extensible)
  addonLeft?: NodeBadgeData;
  addonRight?: NodeBadgeData;
  addonBottom?: NodeBadgeData;
};

export function useNodeRenderer({ id, type, data }: UseNodeRendererProps): UseNodeRendererReturn {
  const connection = useConnection();
  const { validateNode } = useNodeValidation();
  const { getNodeConfig } = useWorkflow();

  // Simple mouse handlers for future extensibility
  const handleMouseEnter = () => {};
  const handleMouseLeave = () => {};

  // Get node type from registry
  const nodeTypeName = (typeof data?.type === 'string' ? data.type : type) || type;
  const nodeType = useMemo(() => getNodeConfig(nodeTypeName), [nodeTypeName, getNodeConfig]);

  // Validation - use the returned result directly
  const hasValidationError = useMemo(() => {
    if (!id) return false;
    const node = {
      id,
      type: nodeTypeName,
      data,
      position: { x: 0, y: 0 },
    };
    const result = validateNode(node);
    return !result.isValid;
  }, [id, nodeTypeName, data, validateNode]);

  const isUnsavedAsync = Boolean(data?.isUnsavedAsync);
  const hasErrors = hasValidationError || isUnsavedAsync;

  // Connection state
  const isSourceNode = connection.inProgress && connection.fromNode.id === id;

  // Extract display info from node type
  const { description: nodeTypeDescription } = nodeType || {};
  const {
    displayName = 'Unknown',
    iconInfo,
    category,
    description: defaultDescription = '',
  } = nodeTypeDescription || {};

  const label = (typeof data?.name === 'string' ? data.name : null) || displayName;
  const description =
    (typeof data?.description === 'string' ? data.description : null) || defaultDescription;

  // Build generic addon badges (extensible for any use case)
  const addonLeft = useMemo((): NodeBadgeData | undefined => {
    const executionCount = (data?.executionCount as number) || 0;
    if (executionCount <= 0) return undefined;

    return {
      content: executionCount,
      tooltip: `${executionCount} execution${executionCount > 1 ? 's' : ''}`,
      variant: 'accent',
    };
  }, [data?.executionCount]);

  const addonRight = useMemo((): NodeBadgeData | undefined => {
    const executionStatus = data?.executionStatus as string | undefined;
    if (!executionStatus) return undefined;

    const statusIcons: Record<string, string> = {
      pending: '⏸',
      running: '▶',
      completed: '✓',
      failed: '✗',
    };

    const statusVariants: Record<string, NodeBadgeData['variant']> = {
      pending: 'default',
      running: 'info',
      completed: 'success',
      failed: 'error',
    };

    return {
      content: statusIcons[executionStatus] || '',
      tooltip: `Status: ${executionStatus}`,
      variant: statusVariants[executionStatus] || 'default',
    };
  }, [data?.executionStatus]);

  const addonBottom = useMemo((): NodeBadgeData | undefined => {
    const artifactCount = (data?.artifactCount as number) || 0;
    if (artifactCount <= 0) return undefined;

    return {
      content: (
        <Flex gap="2" flexDirection="row" alignItems="center">
          <IconBox size="12" />
          <Text fontSize="xs" fontWeight="600">
            {artifactCount}
          </Text>
        </Flex>
      ),
      tooltip: `${artifactCount} artifact${artifactCount > 1 ? 's' : ''}`,
      variant: 'purple',
    };
  }, [data?.artifactCount]);

  return {
    nodeType,
    nodeTypeName,
    displayName,
    description,
    iconInfo,
    category,
    label,
    hasErrors,
    isUnsavedAsync,
    isSourceNode,
    handleMouseEnter,
    handleMouseLeave,
    addonLeft,
    addonRight,
    addonBottom,
  };
}
